import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import {
  ArrowRight, ArrowLeft, Link2, CheckCircle2,
  Loader2, Users, RefreshCw, Crown, UserCheck, Copy, Mail, LogOut
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STEP_LABELS_LEAD = ["Role", "Connect FUB", "Select Agent", "Create Team", "Sync"];
const STEP_LABELS_MEMBER = ["Role", "Join Team", "Select Agent", "Sync"];

function ProgressBar({ current, total, labels }) {
  const pct = Math.round((current / (total - 1)) * 100);
  return (
    <div className="mb-8">
      {/* Bar */}
      <div className="relative h-2 bg-slate-200 rounded-full overflow-hidden mb-3">
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${pct}%`, backgroundColor: "#FF5C35" }}
        />
      </div>
      {/* Labels */}
      <div className="flex justify-between">
        {labels.map((label, i) => (
          <span
            key={i}
            className={`text-[10px] font-medium transition-colors ${
              i <= current ? "text-[#FF5C35]" : "text-slate-400"
            }`}
            style={{ width: `${100 / labels.length}%`, textAlign: i === 0 ? "left" : i === labels.length - 1 ? "right" : "center" }}
          >
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Onboarding() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();

  // Lead flow:   role -> fub-key -> fub-agent -> team-create -> sync
  // Member flow: role -> team-member-join -> fub-key -> fub-agent -> sync
  const [step, setStep] = useState("role");
  const [role, setRole] = useState(null); // "lead" | "member"

  // Team Lead state
  const [teamName, setTeamName] = useState("");
  const [createdTeam, setCreatedTeam] = useState(null); // { id, join_code }
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [copied, setCopied] = useState(false);

  // Team Member state
  const [joinCode, setJoinCode] = useState("");
  const [joiningTeam, setJoiningTeam] = useState(false);
  const [joinError, setJoinError] = useState("");

  // FUB path state
  const [fubKey, setFubKey] = useState("");
  const [fubValidating, setFubValidating] = useState(false);
  const [fubError, setFubError] = useState("");
  const [fubAccount, setFubAccount] = useState(null);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);

  // Completion state
  const [completing, setCompleting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncDone, setSyncDone] = useState(false);

  // ── Team Lead: Create team ──
  const handleCreateTeam = async () => {
    if (!teamName.trim()) return;
    setCreatingTeam(true);
    try {
      const { data } = await axios.post(`${API}/api/teams`, { name: teamName.trim() });
      setCreatedTeam(data);
    } catch (err) {
      console.error(err);
    } finally {
      setCreatingTeam(false);
    }
  };

  const copyCode = () => {
    if (createdTeam?.join_code) {
      navigator.clipboard.writeText(createdTeam.join_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getShareEmail = () => {
    const code = createdTeam?.join_code || "------";
    const subject = encodeURIComponent(`Join ${teamName} on Relay`);
    const body = encodeURIComponent(
      `Hey! I've set up our team on Relay for sending texts to our leads.\n\n` +
      `Here's how to join:\n` +
      `1. Go to the Relay app and create an account\n` +
      `2. During onboarding, select "Team Member / Agent"\n` +
      `3. Enter this team code: ${code}\n\n` +
      `Make sure to use the same email you use in FollowUp Boss!\n\n` +
      `— ${user?.name || "Your Team Lead"}`
    );
    return `mailto:?subject=${subject}&body=${body}`;
  };

  // ── Team Member: Join team ──
  const handleJoinTeam = async () => {
    if (!joinCode.trim()) return;
    setJoiningTeam(true);
    setJoinError("");
    try {
      await axios.post(`${API}/api/teams/join`, { join_code: joinCode.trim().toUpperCase() });
      // Skip FUB key step for members — use team lead's key to fetch agents
      await loadTeamAgents();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setJoinError(typeof detail === "string" ? detail : "Invalid team code. Please check with your team lead.");
    } finally {
      setJoiningTeam(false);
    }
  };

  // ── Load agents using team lead's FUB key (for members) ──
  const loadTeamAgents = async () => {
    try {
      const { data } = await axios.get(`${API}/api/onboarding/team-agents`);
      if (data.agents?.length > 0) {
        setFubAccount(data.account_name);
        setAgents(data.agents);
        if (data.agents.length === 1) setSelectedAgent(data.agents[0]);
        setStep("fub-agent");
      } else {
        // No FUB key configured by lead — skip to sync
        setStep("sync");
      }
    } catch {
      setStep("sync");
    }
  };

  // ── FUB validation ──
  const validateFubKey = async () => {
    setFubValidating(true);
    setFubError("");
    try {
      const { data } = await axios.post(`${API}/api/onboarding/validate-fub`, {
        fub_api_key: fubKey,
      });
      if (data.valid) {
        setFubAccount(data.account_name);
        setAgents(data.agents || []);
        if (data.agents?.length === 1) {
          setSelectedAgent(data.agents[0]);
        }
        setStep("fub-agent");
      } else {
        setFubError(data.message || "Invalid API key");
      }
    } catch {
      setFubError("Could not validate key. Check your connection.");
    } finally {
      setFubValidating(false);
    }
  };

  // ── Complete onboarding (called after agent select for leads, or after agent select for members) ──
  const completeOnboarding = async () => {
    setCompleting(true);
    try {
      const payload = {
        setup_method: "fub",
        fub_api_key: fubKey,
        name: selectedAgent?.name || user?.name,
        fub_user_id: selectedAgent?.id || null,
      };
      const { data } = await axios.post(`${API}/api/onboarding/complete`, payload);
      setUser(data);

      // Lead goes to team creation next, member goes to sync
      if (role === "lead") {
        setStep("team-create");
      } else {
        setStep("sync");
      }
    } catch {
      navigate("/dashboard");
    } finally {
      setCompleting(false);
    }
  };

  const skipFub = async () => {
    setCompleting(true);
    try {
      const payload = { setup_method: "manual", name: user?.name };
      const { data } = await axios.post(`${API}/api/onboarding/complete`, payload);
      setUser(data);
      navigate("/dashboard");
    } catch {
      navigate("/dashboard");
    } finally {
      setCompleting(false);
    }
  };

  const [syncProgress, setSyncProgress] = useState(0);

  const syncContacts = async () => {
    setSyncing(true);
    setSyncProgress(0);
    try {
      await axios.post(`${API}/api/fub/sync`);
      // Poll for progress
      const poll = setInterval(async () => {
        try {
          const r = await axios.get(`${API}/api/fub/sync-status`);
          setSyncProgress(r.data.synced || 0);
          if (r.data.status === "done" || r.data.status === "error") {
            clearInterval(poll);
            setSyncDone(true);
            setSyncing(false);
          }
        } catch { /* ignore */ }
      }, 3000);
    } catch {
      setSyncDone(true);
      setSyncing(false);
    }
  };

  // Lead: role(0) -> fub-key(1) -> fub-agent(2) -> team-create(3) -> sync(4) = 5 steps
  // Member: role(0) -> team-member-join(1) -> fub-agent(2) -> sync(3) = 4 steps
  const totalSteps = role === "member" ? 4 : 5;
  const getCurrentStepNum = () => {
    if (step === "role") return 0;
    if (role === "lead") {
      if (step === "fub-key") return 1;
      if (step === "fub-agent") return 2;
      if (step === "team-create") return 3;
      if (step === "sync") return 4;
    } else {
      if (step === "team-member-join") return 1;
      if (step === "fub-agent") return 2;
      if (step === "sync") return 3;
    }
    return 0;
  };

  // Back button targets
  const getFubKeyBackStep = () => {
    if (role === "lead") return "role";
    return "team-member-join";
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#F4F5F7" }}>
      <div className="w-full max-w-lg mx-4">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img src="/logo.svg" alt="Relay" className="h-16 w-auto" />
          </div>

          {/* ── Step: Role Picker ── */}
          {step === "role" && (
            <>
              <h1 className="text-2xl font-semibold text-center mb-2" style={{ color: "#0F1419" }}>
                Welcome to Relay
              </h1>
              <p className="text-slate-500 text-sm text-center mb-8">
                What's your role on the team?
              </p>

              <div className="space-y-3">
                <button
                  onClick={() => { setRole("lead"); setStep("fub-key"); }}
                  className="w-full flex items-center gap-4 p-4 rounded-lg border-2 border-slate-200 hover:border-[#FF5C35] transition-colors text-left group"
                >
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: "#FFF0EB" }}>
                    <Crown size={22} style={{ color: "#FF5C35" }} />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium" style={{ color: "#0F1419" }}>Team Lead / Broker</p>
                    <p className="text-sm text-slate-500">I'm setting up Relay for my team</p>
                  </div>
                  <ArrowRight size={18} className="text-slate-400 group-hover:text-[#FF5C35] transition-colors" />
                </button>

                <button
                  onClick={() => { setRole("member"); setStep("team-member-join"); }}
                  className="w-full flex items-center gap-4 p-4 rounded-lg border-2 border-slate-200 hover:border-[#FF5C35] transition-colors text-left group"
                >
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: "#FFF0EB" }}>
                    <UserCheck size={22} style={{ color: "#FF5C35" }} />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium" style={{ color: "#0F1419" }}>Team Member / Agent</p>
                    <p className="text-sm text-slate-500">I have a team code from my lead</p>
                  </div>
                  <ArrowRight size={18} className="text-slate-400 group-hover:text-[#FF5C35] transition-colors" />
                </button>
              </div>
            </>
          )}

          {/* ── Team Member: Enter Code ── */}
          {step === "team-member-join" && (
            <>
              <ProgressBar current={1} total={totalSteps} labels={STEP_LABELS_MEMBER} />
              <h2 className="text-xl font-semibold mb-1" style={{ color: "#0F1419" }}>
                Join Your Team
              </h2>
              <p className="text-sm text-slate-500 mb-6">
                Enter the team code your lead shared with you.
              </p>

              {joinError && (
                <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                  {joinError}
                </div>
              )}

              <div className="mb-6">
                <label className="block text-sm font-medium mb-1.5" style={{ color: "#0F1419" }}>Team Code</label>
                <input
                  type="text"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  className="input font-mono text-center text-xl tracking-[0.2em]"
                  placeholder="ABC123"
                  maxLength={6}
                  autoFocus
                />
              </div>

              <div className="flex gap-3">
                <button onClick={() => { setStep("role"); setRole(null); setJoinError(""); }} className="btn-secondary">
                  <ArrowLeft size={16} /> Back
                </button>
                <button
                  onClick={handleJoinTeam}
                  disabled={joinCode.trim().length < 4 || joiningTeam}
                  className="btn-primary flex-1 justify-center"
                >
                  {joiningTeam ? (
                    <span className="flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Joining...</span>
                  ) : (
                    <span className="flex items-center gap-2">Join Team <ArrowRight size={16} /></span>
                  )}
                </button>
              </div>
            </>
          )}

          {/* ── FUB: Enter API Key ── */}
          {step === "fub-key" && (
            <>
              <ProgressBar current={getCurrentStepNum()} total={totalSteps} labels={role === "lead" ? STEP_LABELS_LEAD : STEP_LABELS_MEMBER} />
              <h2 className="text-xl font-semibold mb-1" style={{ color: "#0F1419" }}>
                Connect FollowUp Boss
              </h2>
              <p className="text-sm text-slate-500 mb-6">
                {role === "lead"
                  ? "Your FUB API key is required to set up your team. Find it in FUB under Admin → API."
                  : "Enter your FUB API key to connect your account. Find it in FUB under Admin → API."}
              </p>

              {fubError && (
                <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                  {fubError}
                </div>
              )}

              <div className="mb-6">
                <label className="block text-sm font-medium mb-1.5" style={{ color: "#0F1419" }}>API Key</label>
                <input
                  type="text"
                  value={fubKey}
                  onChange={(e) => setFubKey(e.target.value)}
                  className="input"
                  placeholder="fka_xxxxxxxxxxxxxxxxxxxx"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setFubError("");
                    setStep(getFubKeyBackStep());
                    if (role === "lead") setRole(null);
                  }}
                  className="btn-secondary"
                >
                  <ArrowLeft size={16} /> Back
                </button>
                <button
                  onClick={validateFubKey}
                  disabled={!fubKey.trim() || fubValidating}
                  className="btn-primary flex-1 justify-center"
                >
                  {fubValidating ? (
                    <span className="flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Validating...</span>
                  ) : (
                    <span className="flex items-center gap-2">Validate & Connect <ArrowRight size={16} /></span>
                  )}
                </button>
              </div>

              {/* Only members can skip FUB — leads must connect */}
              {role === "member" && (
                <button
                  onClick={skipFub}
                  disabled={completing}
                  className="w-full mt-3 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                >
                  Skip for now — I'll connect later in Settings
                </button>
              )}
            </>
          )}

          {/* ── FUB: Select Agent ── */}
          {step === "fub-agent" && (
            <>
              <ProgressBar current={getCurrentStepNum()} total={totalSteps} labels={role === "lead" ? STEP_LABELS_LEAD : STEP_LABELS_MEMBER} />
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 size={20} style={{ color: "#FF5C35" }} />
                <h2 className="text-xl font-semibold" style={{ color: "#0F1419" }}>
                  Connected to {fubAccount}
                </h2>
              </div>
              <p className="text-sm text-slate-500 mb-6">
                {agents.length > 1
                  ? "Select which agent you are on this team."
                  : "We found your agent profile."}
              </p>

              <div className="space-y-2 mb-6 max-h-64 overflow-y-auto">
                {agents.map((agent) => (
                  <button
                    key={agent.id}
                    onClick={() => setSelectedAgent(agent)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-colors text-left ${
                      selectedAgent?.id === agent.id
                        ? "border-[#FF5C35] bg-[#FFF0EB]"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium shrink-0"
                      style={{ backgroundColor: "#FF5C35" }}
                    >
                      {agent.name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <p className="font-medium text-sm" style={{ color: "#0F1419" }}>{agent.name}</p>
                      <p className="text-xs text-slate-500">{agent.email}{agent.role ? ` · ${agent.role}` : ""}</p>
                    </div>
                    {selectedAgent?.id === agent.id && (
                      <CheckCircle2 size={18} className="ml-auto" style={{ color: "#FF5C35" }} />
                    )}
                  </button>
                ))}
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep(role === "lead" ? "fub-key" : "team-member-join")} className="btn-secondary">
                  <ArrowLeft size={16} /> Back
                </button>
                <button
                  onClick={completeOnboarding}
                  disabled={!selectedAgent || completing}
                  className="btn-primary flex-1 justify-center"
                >
                  {completing ? (
                    <span className="flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Saving...</span>
                  ) : (
                    <span className="flex items-center gap-2">Continue <ArrowRight size={16} /></span>
                  )}
                </button>
              </div>
            </>
          )}

          {/* ── Team Lead: Create Team (after FUB is connected) ── */}
          {step === "team-create" && !createdTeam && (
            <>
              <ProgressBar current={3} total={totalSteps} labels={STEP_LABELS_LEAD} />
              <h2 className="text-xl font-semibold mb-1" style={{ color: "#0F1419" }}>
                Name Your Team
              </h2>
              <p className="text-sm text-slate-500 mb-6">
                This is how your team will appear in Relay. Your agents will use a join code to connect.
              </p>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-1.5" style={{ color: "#0F1419" }}>Team Name</label>
                <input
                  type="text"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  className="input"
                  placeholder="e.g. Petrosian Team"
                  autoFocus
                />
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep("fub-agent")} className="btn-secondary">
                  <ArrowLeft size={16} /> Back
                </button>
                <button
                  onClick={handleCreateTeam}
                  disabled={!teamName.trim() || creatingTeam}
                  className="btn-primary flex-1 justify-center"
                >
                  {creatingTeam ? (
                    <span className="flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Creating...</span>
                  ) : (
                    <span className="flex items-center gap-2">Create Team <ArrowRight size={16} /></span>
                  )}
                </button>
              </div>

              <button
                onClick={() => setStep("sync")}
                className="w-full mt-3 text-sm text-slate-500 hover:text-slate-700 transition-colors"
              >
                Skip — I'll set up a team later
              </button>
            </>
          )}

          {/* ── Team Lead: Show Code ── */}
          {step === "team-create" && createdTeam && (
            <>
              <ProgressBar current={3} total={totalSteps} labels={STEP_LABELS_LEAD} />
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: "#FFF0EB" }}>
                  <CheckCircle2 size={28} style={{ color: "#FF5C35" }} />
                </div>
                <h2 className="text-xl font-semibold mb-1" style={{ color: "#0F1419" }}>
                  Team Created!
                </h2>
                <p className="text-sm text-slate-500">
                  Share this code with your agents so they can join.
                </p>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 text-center mb-4">
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Your Team Code</p>
                <p className="text-3xl font-mono font-bold tracking-[0.2em]" style={{ color: "#0F1419" }}>
                  {createdTeam.join_code}
                </p>
              </div>

              <div className="flex gap-2 mb-6">
                <button
                  onClick={copyCode}
                  className="btn-secondary flex-1 justify-center text-sm"
                >
                  <Copy size={14} /> {copied ? "Copied!" : "Copy Code"}
                </button>
                <a
                  href={getShareEmail()}
                  className="btn-secondary flex-1 justify-center text-sm inline-flex items-center gap-1.5"
                >
                  <Mail size={14} /> Email to Team
                </a>
              </div>

              <button
                onClick={() => setStep("sync")}
                className="btn-primary w-full justify-center"
              >
                <span className="flex items-center gap-2">Continue Setup <ArrowRight size={16} /></span>
              </button>
            </>
          )}

          {/* ── Sync Contacts ── */}
          {step === "sync" && (
            <>
              <ProgressBar current={4} total={totalSteps} labels={role === "lead" ? STEP_LABELS_LEAD : STEP_LABELS_MEMBER} />
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: "#FFF0EB" }}>
                  <Users size={28} style={{ color: "#FF5C35" }} />
                </div>
                <h2 className="text-xl font-semibold mb-1" style={{ color: "#0F1419" }}>
                  Sync Your Contacts
                </h2>
                <p className="text-sm text-slate-500">
                  Pull in your contacts from FollowUp Boss so you can start texting right away.
                </p>
              </div>

              {syncDone ? (
                <div className="text-center mb-6">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <CheckCircle2 size={20} style={{ color: "#FF5C35" }} />
                    <span className="font-medium" style={{ color: "#0F1419" }}>Contacts synced!</span>
                  </div>
                  <button onClick={() => navigate("/dashboard")} className="btn-primary justify-center w-full">
                    Go to Dashboard <ArrowRight size={16} />
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <div className="flex gap-3">
                    <button
                      onClick={() => setStep(role === "lead" ? "team-create" : "fub-agent")}
                      className="btn-secondary"
                    >
                      <ArrowLeft size={16} /> Back
                    </button>
                    <button onClick={syncContacts} disabled={syncing} className="btn-primary justify-center flex-1">
                      {syncing ? (
                        <span className="flex items-center gap-2"><RefreshCw size={16} className="animate-spin" /> Syncing... {syncProgress > 0 ? `${syncProgress.toLocaleString()} imported` : ""}</span>
                      ) : (
                        <span className="flex items-center gap-2"><RefreshCw size={16} /> Sync Contacts Now</span>
                      )}
                    </button>
                  </div>
                  <button onClick={() => navigate("/dashboard")} className="text-sm text-slate-500 hover:text-slate-700 transition-colors">
                    Skip for now — I'll sync later
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Sign out link */}
        <button
          onClick={async () => { await logout(); navigate("/login"); }}
          className="flex items-center justify-center gap-1.5 mx-auto mt-5 text-sm text-slate-400 hover:text-slate-600 transition-colors"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </div>
  );
}
